import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import logoSample from "../../assets/images/logo_sample.png";
import Pagination from "../../components/Pagination/Pagination";
import formatDate from "../Utility/DateFormat";
import {
  confirmDelete,
  createOrUpdateClient,
  getClients as fetchClients,
  viewClientDetails,
} from "../Clients/ClientService";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { getClient } from "../../services/ValitanApiService";
import { ConfirmDialog } from "primereact/confirmdialog";
import { LoadingContext } from "../../../src/Pages/Utility/spinner/LoadingContext";

const ClientListTables = () => {
  const { setIsLoading } = useContext(LoadingContext);
  document.title = "Clients | Valitan";

  const [clients, setClients] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientTin, setClientTin] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientMobileNumber, setClientMobileNumber] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientLogo, setClientLogo] = useState("");
  const [clientIsPrivate, setClientIsPrivate] = useState("");
  const [clientCreatedAt, setClientCreatedAt] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({
    clientName: "",
    clientTin: "",
    clientMobileNumber: "",
    clientIsPrivate: "",
  });
  const [editingClientId, setEditingClientId] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    sort: {},
  });

  const loadClients = useCallback(async (page = 0, size = 10) => {
    try {
      setIsLoading(true);
      setLoading(true);
      const { clients, paginationInfo } = await fetchClients(page, size);
      setClients(clients || []);
      setPaginationInfo(paginationInfo || {
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: 10,
        sort: {},
      });
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients(0, paginationInfo.pageSize);
  }, [loadClients, paginationInfo.pageSize]);

  const handleNextPage = () => {
    if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      loadClients(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber > 0) {
      loadClients(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
    }
  };

  const openModalForDetails = async (id) => {
    try {
      setIsLoading(true);
      await viewClientDetails(
        id,
        setLoading,
        setClientName,
        setClientTin,
        setClientAddress,
        setClientMobileNumber,
        setClientEmail,
        setClientLogo,
        setClientIsPrivate,
        setClientCreatedAt,
        setDetailsModalOpen
      );
    } catch (error) {
      console.error("Error fetching client details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openModalForEdit = async (id) => {
    setEditingClientId(id);
    try {
      setIsLoading(true);
      const response = await getClient(id);
      if (response?.data?.data) {
        const field = response.data.data;
        setClientName(field.name);
        setClientTin(field.tin);
        setClientAddress(field.address);
        setClientMobileNumber(field.mobile_number);
        setClientEmail(field.email);
        setClientLogo(field.logo);
        setClientIsPrivate(field.is_private);
        setClientCreatedAt(field.created_at);
        setModalOpen(true);
      } else {
        console.error("Client data is undefined");
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleModal = () => {
    console.log("Toggling modal. Current state:", modalOpen);
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      setClientName("");
      setClientTin("");
      setClientAddress("");
      setClientMobileNumber("");
      setClientEmail("");
      setClientLogo("");
      setClientIsPrivate("");
      setErrors({
        clientName: "",
        clientTin: "",
        clientMobileNumber: "",
        clientIsPrivate: "",
      });
      setEditingClientId(null);
    }
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setClientLogo(file);

    // Generate a preview URL for the logo
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Clients management" breadcrumbItem="Clients" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <Button
                      color="primary"
                      className="add-btn float-end"
                      id="TooltipTop"
                      onClick={toggleModal}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add
                    </Button>
                  </h4>
                </CardHeader>

                <CardBody>
                  <div>
                    <Row className="g-4 mb-3">
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search..."
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {loading ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      <div className="table-responsive table-card mt-3 mb-1">
                        <table
                          className="table align-middle table-nowrap"
                          id="customerTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th className="sort" data-sort="email">
                                #
                              </th>
                              <th className="sort" data-sort="email">
                                TIN
                              </th>
                              <th className="sort" data-sort="email">
                                Name
                              </th>
                              <th className="sort" data-sort="phone">
                                Mobile Number
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {clients.length > 0 ? (
                              clients.map((data, index) => (
                                <tr key={data.id}>
                                  <td>{startIndex + index}</td>
                                  <td>{data.tin}</td>
                                  <td>{data.name}</td>
                                  <td>{data.mobile_number}</td>
                                  <td>
                                    <div
                                      className="btn-group"
                                      role="group"
                                      aria-label="Basic example"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => openModalForDetails(data.id)}
                                        className="btn btn-primary btn-sm"
                                      >
                                        <i className="ri-eye-line"></i>
                                      </button>
                                      <button
                                        id="TooltipTop"
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => openModalForEdit(data.id)}
                                      >
                                        <i className="ri-edit-box-line"></i>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => confirmDelete(data.id, loadClients)}
                                        className="btn btn-danger btn-sm"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" style={{ textAlign: "center" }}>
                                  No records found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <Pagination
                      paginationInfo={paginationInfo}
                      handleNextPage={handleNextPage}
                      handlePreviousPage={handlePreviousPage}
                      onPageChange={(pageNumber, pageSize) => loadClients(pageNumber, pageSize)}
                      onPageSizeChange={(newPageSize) => {
                        setPaginationInfo((prev) => ({
                          ...prev,
                          pageSize: newPageSize,
                          pageNumber: 0, // Reset to the first page
                        }));
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ConfirmDialog */}
      <ConfirmDialog />

      {/* ToastContainer for displaying notifications */}
      <ToastContainer />

      <Modal isOpen={modalOpen} toggle={toggleModal} backdrop={"static"}>
        <ModalHeader toggle={toggleModal}>
          {editingClientId ? "Edit Client" : "Add Client"}
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(evt) =>
              createOrUpdateClient(
                evt,
                clientName,
                clientTin,
                clientMobileNumber,
                clientAddress,
                clientEmail,
                clientLogo,
                clientIsPrivate,
                editingClientId,
                loadClients,
                toggleModal,
                setErrors
              )
            }
          >
            <Row>
              <FormGroup className="col-6">
                <Label for="clientTin">TIN</Label>
                <Input
                  type="text"
                  name="clientTin"
                  id="clientTin"
                  placeholder="Enter TIN"
                  value={clientTin}
                  onChange={(e) => setClientTin(e.target.value)}
                  invalid={!!errors.clientTin}
                />
                {errors.clientTin && (
                  <div className="invalid-feedback">{errors.clientTin}</div>
                )}
              </FormGroup>
              <FormGroup className="col-6">
                <Label for="clientIsPrivate">Client Type</Label>
                <Input
                  type="select"
                  name="clientIsPrivate"
                  id="clientIsPrivate"
                  value={clientIsPrivate}
                  onChange={(e) => setClientIsPrivate(e.target.value)}
                >
                  <option value="">Select type</option>
                  <option value="No">Government</option>
                  <option value="Yes">Private</option>
                </Input>
                {errors.clientIsPrivate && (
                  <div className="invalid-feedback">{errors.clientIsPrivate}</div>
                )}
              </FormGroup>
            </Row>

            <FormGroup>
              <Label for="clientName">Name</Label>
              <Input
                type="text"
                name="clientName"
                id="clientName"
                placeholder="Enter Name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                invalid={!!errors.clientName}
              />
              {errors.clientName && (
                <div className="invalid-feedback">{errors.clientName}</div>
              )}
            </FormGroup>

            <Row>
              <FormGroup className="col-6">
                <Label for="clientMobileNumber">Mobile Number</Label>
                <Input
                  type="text"
                  name="clientMobileNumber"
                  id="clientMobileNumber"
                  placeholder="Enter Mobile Number"
                  value={clientMobileNumber}
                  onChange={(e) => setClientMobileNumber(e.target.value)}
                  invalid={!!errors.clientMobileNumber}
                />
                {errors.clientMobileNumber && (
                  <div className="invalid-feedback">{errors.clientMobileNumber}</div>
                )}
              </FormGroup>
              <FormGroup className="col-6">
                <Label for="clientEmail">Email</Label>
                <Input
                  type="text"
                  name="clientEmail"
                  id="clientEmail"
                  placeholder="Enter Email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                />
              </FormGroup>
            </Row>

            <FormGroup>
              <Label for="clientAddress">Address</Label>
              <Input
                type="text"
                name="clientAddress"
                id="clientAddress"
                placeholder="Enter Address"
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
              />
            </FormGroup>

            <Row>
              <FormGroup className="col-7">
                <Label for="clientLogo">Logo</Label>
                <Input
                  type="file"
                  name="clientLogo"
                  id="clientLogo"
                  onChange={handleFileChange}
                />
              </FormGroup>
              {logoPreview && (
                <div className="col-4 float-end">
                  <img
                    src={logoPreview}
                    className="avatar-md rounded-circle img-thumbnail"
                    alt="Logo Preview"
                    style={{ width: "100px", height: "100px" }}
                  />
                </div>
              )}
            </Row>

            <Button type="submit" color="primary" className="float-end">
              {editingClientId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={detailsModalOpen}
        toggle={() => setDetailsModalOpen(false)}
        backdrop={"static"}
        className="modal-lg"
      >
        <ModalHeader toggle={() => setDetailsModalOpen(false)}>
          <span style={{ marginRight: "2em" }}>
            <strong>Details</strong>
          </span>
          {clientTin}
        </ModalHeader>
        <ModalBody>
          <Row className="d-flex">
            <div className="col-md-8">
              <div className="plan-features mt-2 mb-3">
                <p className="d-flex align-items-center">
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-18 align-middle text-primary me-2"></i>{" "}
                  <span style={{ marginRight: "2em" }}>
                    <strong>Name:</strong>
                  </span>
                  {clientName}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>{" "}
                  <span style={{ marginRight: "2em" }}>
                    <strong>Mobile Number:</strong>
                  </span>
                  {clientMobileNumber}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>{" "}
                  <span style={{ marginRight: "2em" }}>
                    <strong>Email:</strong>
                  </span>
                  {clientEmail}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>{" "}
                  <span style={{ marginRight: "2em" }}>
                    <strong>Address:</strong>
                  </span>
                  {clientAddress}
                </p>
                <p>
                  <i className="mdi mdi-checkbox-marked-circle-outline font-size-16 align-middle text-primary me-2"></i>{" "}
                  <span style={{ marginRight: "2em" }}>
                    <strong>Is Private:</strong>
                  </span>
                  {clientIsPrivate}
                </p>
              </div>
            </div>

            <div className="col-3 ms-auto">
              <img
                src={logoSample}
                className="avatar-md img-thumbnail"
                alt="Logo Preview"
                style={{ width: "150px", height: "150px" }}
              />
            </div>
          </Row>
        </ModalBody>
        <ModalFooter>
          <p>
            <i className="mdi mdi-calendar font-size-14 align-middle text-primary"></i>{" "}
            <span style={{ marginRight: "2em" }}>
              <small>Created at:</small>
            </span>
            <small>{formatDate(clientCreatedAt)}</small>
          </p>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ClientListTables;
